// MUI Imports
import type { Theme } from '@mui/material/styles'

// Type Imports
import type { Settings } from '@core/contexts/settingsContext'
import type { SystemMode, Skin } from '@core/types'

// Theme Options Imports
import overrides from './overrides'
import colorSchemes from './colorSchemes'
import spacing from './spacing'
import shadows from './shadows'
import customShadows from './customShadows'
import typography from './typography'

// 'latin-ext' aggiunto col round i18n onda 4 (#171): cs/sk/pl/hu/hr/ro/lt/lv/tr
// scrivono ą ř ő ș ğ ū — glifi che stanno in latin-ext, non in latin. Senza il
// sottoinsieme il browser risolveva QUELLE LETTERE con un font di sistema, in
// mezzo a parole rese in Public Sans: la parola si spezzava tipograficamente.
// Public Sans NON copre greco, cirillico né armeno (non esistono come subsets:
// la famiglia ha solo latin, latin-ext, vietnamese). Fino al 2026-08-07 bg, el,
// ru, sr, uk e hy cadevano sul font di sistema per l'INTERA stringa: coerenti al
// loro interno ma con una faccia estranea alla chrome — «un font un po' troppo
// stock», la segnalazione di Luca.
//
// Rimedio (2026-08-07): non si cambia famiglia, si ESTENDE la catena. Noto Sans
// copre cirillico e greco, Noto Sans Armenian l'armeno; entrambe sono disegnate
// per convivere con altre grottesche, quindi il salto di faccia non si vede.
// Il browser risolve PER GLIFO lungo la catena: il latino resta Public Sans
// ovunque, e solo le lettere che Public Sans non ha scendono al fallback. Vale
// anche dentro la stessa riga (un nome di VM latino in una GUI in greco).
//
// Le tre famiglie NON arrivano piu' da `next/font/google` (round #332, ratifica R12
// del 2026-08-11): i woff2 sono vendorizzati in `src/assets/fonts/` e le @font-face
// stanno in `vendored-fonts.css`, importato dal layout di radice. Il build non tocca
// piu' la rete — la rotazione degli URL a monte aveva gia' rotto una compilazione, e
// TVirt si installa in cluster senza Internet. Per rigenerarle:
// `node scripts/vendor-google-fonts.mjs` (a mano, mai dal build).
//
// R23 (2026-08-13) — LE FACCE DI FALLBACK INTERMEDIE NON CI SONO PIU'. `next/font`
// ne emetteva una per famiglia, `local("Arial")` SENZA `unicode-range`: una faccia
// senza unicode-range rivendica U+0-10FFFF, quindi `Public Sans Fallback` in seconda
// posizione si prendeva cirillico e greco prima che il browser arrivasse a Noto Sans.
// MISURATO in Chromium con un Arial vero installato: i glifi cirillici li rendeva
// «Arial», e `noto-sans-cyrillic.woff2` non veniva nemmeno scaricato — il rimedio i18n
// del 2026-08-07 era inerte su ogni macchina Windows o macOS. Ora la faccia
// ricalibrata e' UNA SOLA e sta in CODA, dove non puo' intercettare niente; sono le
// metriche di Public Sans perche' e' Public Sans a rendere quasi tutto il testo, e
// cosi' il layout pre-swap resta identico a quello di prima (misurato: stesse
// larghezze al centesimo di pixel, stessa altezza di riga).
//
// NON e' cosmetica — cambiarne l'ordine cambia quale font risolve un glifo. La stringa
// e' GENERATA nel lock da `scripts/vendor-google-fonts.mjs` e presidiata da
// `test/build-hermetic.test.ts`, che la confronta con `vendored-fonts.lock.json`: non
// si tiene allineata a mano.
const fontStack = "'Public Sans', 'Noto Sans', 'Noto Sans Armenian', 'Public Sans Fallback'"

const theme = (settings: Settings, mode: SystemMode, direction: Theme['direction']): Theme => {
  return {
    direction,
    components: overrides(settings.skin as Skin),
    colorSchemes: colorSchemes(settings.skin as Skin),
    ...spacing,
    shape: {
      borderRadius: 6,
      customBorderRadius: {
        xs: 2,
        sm: 4,
        md: 6,
        lg: 8,
        xl: 10
      }
    },
    shadows: shadows(mode),
    typography: typography(fontStack),
    customShadows: customShadows(mode),
    mainColorChannels: {
      light: '47 43 61',
      dark: '225 222 245',
      lightShadow: '47 43 61',
      darkShadow: '19 17 32'
    }
  } as Theme
}

export default theme
